import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Users, Package, Factory,
  Truck, Settings, LogOut, Warehouse, Boxes, Wrench,
  Clock, XCircle, FileText, ChevronDown, ChevronRight,
  Building, Scale, Disc, Link2, List, ShoppingCart, Timer, ClipboardCheck, Send, BarChart3,
  Layers, PlayCircle
} from 'lucide-react';
import { cn } from '../lib/utils';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_39bdae91-5bb2-446a-a4be-d388ee59164b/artifacts/7o21ivqd_Southern%20Logo.jpg";

const Sidebar = () => {
  const { user, logout, isAdmin, hasRole } = useAuth();
  const navigate = useNavigate();
  const [expandedMenus, setExpandedMenus] = React.useState([]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMenu = (menu) => {
    setExpandedMenus(prev => 
      prev.includes(menu) 
        ? prev.filter(m => m !== menu)
        : [...prev, menu]
    );
  };

  const NavItem = ({ to, icon: Icon, label, roles = [], compact = false }) => {
    if (roles.length > 0 && !hasRole(roles)) return null;

    return (
      <NavLink
        to={to}
        className={({ isActive }) => cn(
          "sidebar-link",
          compact && "py-1.5 text-xs",
          isActive && "active"
        )}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon className={cn("w-4 h-4", compact && "w-3.5 h-3.5")} />
        <span>{label}</span>
      </NavLink>
    );
  };

  // Grouped Masters - Organization & HR
  const orgMasters = [
    { to: '/masters/organizations', icon: Building, label: 'Organizations' },
    { to: '/masters/branches', icon: Building, label: 'Branches' },
    { to: '/masters/stores', icon: Warehouse, label: 'Stores' },
    { to: '/masters/employees', icon: Users, label: 'Employees' },
    { to: '/masters/roles', icon: Settings, label: 'Roles' },
  ];

  // Grouped Masters - Business
  const businessMasters = [
    { to: '/masters/customers', icon: Users, label: 'Customers' },
    { to: '/masters/vendors', icon: Truck, label: 'Vendors' },
    { to: '/masters/items', icon: Package, label: 'Items' },
    { to: '/masters/uoms', icon: Scale, label: 'UOM' },
  ];

  // Grouped Masters - Production
  const productionMasters = [
    { to: '/masters/machines', icon: Factory, label: 'Machines' },
    { to: '/masters/moulds', icon: Disc, label: 'Moulds' },
    { to: '/masters/mould-parts', icon: Link2, label: 'Mould-Part' },
    { to: '/masters/parts', icon: Boxes, label: 'Parts' },
    { to: '/masters/bom', icon: List, label: 'BOM' },
    { to: '/masters/cycle-times', icon: Clock, label: 'Cycle Times' },
    { to: '/masters/rejection-reasons', icon: XCircle, label: 'Rejections' },
    { to: '/masters/shifts', icon: Timer, label: 'Shifts' },
  ];

  const transactionMenuItems = [
    { to: '/transactions/purchases', icon: ShoppingCart, label: 'GRN / Purchase' },
    { to: '/transactions/incoming-inspection', icon: ClipboardCheck, label: 'Inspection' },
    { to: '/transactions/item-requests', icon: FileText, label: 'Material Request' },
    { to: '/transactions/item-responses', icon: Send, label: 'Issue Items' },
    { to: '/transactions/production-templates', icon: Layers, label: 'Prod Templates' },
    { to: '/transactions/production-runs', icon: PlayCircle, label: 'Production Runs' },
    { to: '/transactions/part-inspection', icon: ClipboardCheck, label: 'Part Inspection' },
    { to: '/transactions/customer-pos', icon: FileText, label: 'Customer PO' },
    { to: '/transactions/sales-orders', icon: ShoppingCart, label: 'Sales Orders' },
    { to: '/transactions/dispatch-challans', icon: Truck, label: 'Dispatch' },
  ];

  const reportMenuItems = [
    { to: '/reports/inventory', icon: Package, label: 'Inventory' },
    { to: '/reports/production', icon: Factory, label: 'Production Report' },
    { to: '/reports/procurement-planning', icon: ClipboardCheck, label: 'Procurement Plan' },
  ];

  const MenuSection = ({ title, items, menuKey, roles = ['ADMIN'] }) => {
    if (!hasRole(roles)) return null;
    const isExpanded = expandedMenus.includes(menuKey);
    
    return (
      <div className="mb-1">
        <button
          onClick={() => toggleMenu(menuKey)}
          className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-semibold text-slate-400 hover:text-white transition-colors"
        >
          <span>{title}</span>
          {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </button>
        {isExpanded && (
          <div className="mt-0.5">
            {items.map((item) => (
              <NavItem key={item.to} {...item} compact />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="sidebar-nav w-56 flex flex-col">
      {/* Logo */}
      <div className="px-3 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <img 
            src={LOGO_URL} 
            alt="Southern Plastics" 
            className="w-10 h-10 rounded object-contain bg-white"
          />
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold text-white tracking-tight leading-tight">Southern Plastics</h1>
            <p className="text-[10px] text-slate-500 truncate">India Pvt Ltd</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-2 overflow-y-auto text-sm">
        {/* Dashboard */}
        <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" />

        {/* Masters - Grouped */}
        {isAdmin() && (
          <>
            <div className="mt-3 mb-1 px-3">
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Masters</span>
            </div>
            <MenuSection title="Organization" items={orgMasters} menuKey="org" />
            <MenuSection title="Business" items={businessMasters} menuKey="business" />
            <MenuSection title="Production" items={productionMasters} menuKey="production" roles={['ADMIN', 'PRODUCTION']} />
          </>
        )}

        {/* Transactions */}
        <div className="mt-3 mb-1 px-3">
          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Transactions</span>
        </div>
        {transactionMenuItems.map((item) => (
          <NavItem key={item.to} {...item} />
        ))}

        {/* Reports */}
        <div className="mt-3 mb-1 px-3">
          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Reports</span>
        </div>
        {reportMenuItems.map((item) => (
          <NavItem key={item.to} {...item} />
        ))}

        {/* Audit Logs - Admin Only */}
        {isAdmin() && (
          <div className="mt-3 pt-2 border-t border-slate-800">
            <NavItem to="/audit-logs" icon={FileText} label="Audit Logs" roles={['ADMIN']} />
          </div>
        )}
      </nav>

      {/* User Info & Logout */}
      <div className="border-t border-slate-800 p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-semibold text-white">
            {user && user.first_name ? user.first_name[0] : ''}{user && user.last_name ? user.last_name[0] : ''}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-white truncate">
              {(user && user.first_name)} {(user && user.last_name)}
            </p>
            <p className="text-[10px] text-slate-500 truncate">{(user && user.email)}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-slate-400 hover:text-white hover:bg-slate-800 rounded-sm transition-colors"
          data-testid="logout-button"
        >
          <LogOut className="w-3.5 h-3.5" />
          Logout
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
